// Der Zustand des Spiegels (Avatar-Editor) als reine Logik: welche Farben gewählt sind, welche
// Felder die Palette anbietet und wo das Speichern gerade steht. Kein DOM, kein Three.js – die
// Ansicht dazu steht in `hud/spiegel.ts` (docs/architektur-paket-2.md, Abschnitt „Client“, `hud/`).
//
// Jede Änderung liefert einen neuen Zustand; wo nichts zu ändern ist, kommt derselbe zurück, damit
// ein Vergleich mit === „hat sich nichts geändert“ heißt (wie in auth/zustand.ts).

import { AVATAR_BEREICHE, AVATAR_PALETTE, type AvatarColors } from '@bauernhof/shared';

/**
 * Die wählbaren Bereiche. Nicht `AvatarBereich` aus shared: Dort gehört `gesicht` dazu, und das
 * wählt niemand aus – Augen und Mund bleiben fest (siehe `avatarBauplan`).
 */
export type WahlBereich = keyof AvatarColors;

/** Überschriften der vier Bereiche, wie sie im Spiegel stehen. */
export const BEREICH_TITEL: Readonly<Record<WahlBereich, string>> = {
  haut: 'Haut',
  haar: 'Haare',
  oberteil: 'Oberteil',
  hose: 'Hose',
};

/** Ein Farbfeld zum Anklicken. */
export interface Farbfeld {
  farbe: string;
  gewaehlt: boolean;
}

/** Eine Zeile des Spiegels: ein Bereich mit seiner ganzen Palette. */
export interface Bereichsfelder {
  bereich: WahlBereich;
  titel: string;
  felder: Farbfeld[];
}

/** `waehlt` – der Spieler sucht aus; `speichert` – `PUT /api/avatar` läuft; `fertig` – gespeichert. */
export type SpiegelStand = 'waehlt' | 'speichert' | 'fertig';

export interface SpiegelZustand {
  farben: AvatarColors;
  stand: SpiegelStand;
  /** Kurzer deutscher Satz, wenn das Speichern schiefging; sonst null. */
  fehler: string | null;
  /** Beim ersten Anmelden öffnet sich der Spiegel von selbst und will eine Entscheidung sehen. */
  erstesMal: boolean;
}

/** Frischer Spiegel mit den Farben, die das Konto gerade hat. */
export function spiegelStart(farben: AvatarColors, erstesMal: boolean): SpiegelZustand {
  return { farben: { ...farben }, stand: 'waehlt', fehler: null, erstesMal };
}

/** Die Felder je Bereich: die ganze Palette, die aktuelle Farbe abgehakt. */
export function felderFuer(farben: AvatarColors): Bereichsfelder[] {
  return AVATAR_BEREICHE.map((bereich) => ({
    bereich,
    titel: BEREICH_TITEL[bereich],
    felder: AVATAR_PALETTE[bereich].map((farbe) => ({ farbe, gewaehlt: farbe === farben[bereich] })),
  }));
}

/**
 * Wählt eine Farbe. Angenommen wird nur, was in der Palette dieses Bereichs steht – sonst schickte
 * der Spiegel etwas los, was der Server mit 400 abweist. Während des Speicherns ändert sich nichts.
 */
export function waehleFarbe(zustand: SpiegelZustand, bereich: WahlBereich, farbe: string): SpiegelZustand {
  if (zustand.stand === 'speichert') return zustand;
  if (!AVATAR_PALETTE[bereich].includes(farbe)) return zustand;
  if (zustand.farben[bereich] === farbe && zustand.fehler === null) return zustand;
  return { ...zustand, farben: { ...zustand.farben, [bereich]: farbe }, fehler: null };
}

/** „Fertig“ wurde gedrückt: Das Speichern läuft, die Knöpfe ruhen. */
export function beginneSpeichern(zustand: SpiegelZustand): SpiegelZustand {
  if (zustand.stand === 'speichert') return zustand;
  return { ...zustand, stand: 'speichert', fehler: null };
}

/** Der Server hat die Farben angenommen. */
export function speichernGeglueckt(zustand: SpiegelZustand): SpiegelZustand {
  return { ...zustand, stand: 'fertig', fehler: null };
}

/** Das Speichern ging schief: zurück zur Wahl, mit der Meldung des Servers. */
export function speichernGescheitert(zustand: SpiegelZustand, meldung: string): SpiegelZustand {
  return { ...zustand, stand: 'waehlt', fehler: meldung };
}

/**
 * Ob der Spiegel ohne Speichern zugehen darf. Beim ersten Anmelden nicht: Dort ist die Wahl der
 * Weg ins Spiel. Während des Speicherns auch nicht, sonst liefe die Antwort ins Leere.
 */
export function darfAbbrechen(zustand: SpiegelZustand): boolean {
  return !zustand.erstesMal && zustand.stand !== 'speichert';
}
